import { apis, useTenantQuery } from 'probonio-shared-ui/module/api';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { CurrentCustomBenefitTemplate, CustomBenefitTemplateKeys, useCustomBenefitTemplates } from './useCustomBenefitTemplates';

const emptyTemplate: CurrentCustomBenefitTemplate = {
  key: CustomBenefitTemplateKeys.CUSTOM_BENEFIT,
  benefit: { iconValue: 'Euro', description: '', imageUrl: '', title: '', html: '', defaultMonthlyGrant: 0 },
};

export const useCurrentCustomBenefitTemplate = (): CurrentCustomBenefitTemplate | undefined => {
  const { templateId, customBenefitId } = useParams<{ templateId?: CustomBenefitTemplateKeys; customBenefitId?: string }>();
  const templates = useCustomBenefitTemplates();
  const { data: customBenefit, isFetched } = useTenantQuery(
    ['benefits', 'custom', customBenefitId, apis.customBenefits.getCustomBenefit],
    tenantId =>
      apis.customBenefits
        .getCustomBenefit({
          tenantId,
          customBenefitId: customBenefitId!,
        })
        .then(res => res.data),
    {
      enabled: !!customBenefitId,
    },
  );

  return useMemo<CurrentCustomBenefitTemplate | undefined>(() => {
    if (!isFetched && customBenefitId) {
      return undefined;
    }

    const newBenefit = { customBenefitId, ...emptyTemplate, benefit: { ...emptyTemplate.benefit } };

    if (customBenefit) {
      newBenefit.customBenefit = customBenefit;
      newBenefit.customBenefitId = customBenefit.id;
      newBenefit.benefit.enabled = customBenefit.enabled;
      newBenefit.benefit.iconValue = customBenefit.iconName;
      newBenefit.benefit.showAsTile = customBenefit.showTile;
      newBenefit.benefit.showVisualization = customBenefit.showVisualization;
      newBenefit.benefit.description = '';
      newBenefit.benefit.imageUrl = customBenefit.tileImageURL || '';
      newBenefit.benefit.title = customBenefit.title;
      newBenefit.benefit.html = customBenefit.infoHtml;
      newBenefit.benefit.defaultMonthlyGrant = (customBenefit.defaultMonthlyGrant || 0) / 100;
      newBenefit.benefit.relevantForNonCash = customBenefit.relevantForNonCash;
      newBenefit.benefit.templateKey = customBenefit.templateKey || '';
      return newBenefit;
    }

    if (!templateId) {
      return newBenefit;
    }
    const currentCustomBenefitTemplate = templates[templateId];

    if (!currentCustomBenefitTemplate) {
      return newBenefit;
    }

    newBenefit.key = templateId;
    newBenefit.benefit = currentCustomBenefitTemplate;
    newBenefit.customBenefitId = undefined;
    return newBenefit;
  }, [customBenefit, customBenefitId, isFetched, templateId, templates]);
};
